import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';

// Components
import AddressAutocomplete from '@atoms/AddressAutocomplete';
import ButtonOutlineWithStates from '@atoms/ButtonOutlineWithStates';
import SelectFancy from '@atoms/SelectFancy';
import SubmitButton from '@atoms/SubmitButton';
import TextInput from '@atoms/TextInput';
import WYSIWYG from '@atoms/WYSIWYG';
import { DividerLightGreyStyled } from '@styled';

// Helpers
import { useSuccessListingModal } from '../../modals/SuccessPropertyModal';
import { getErrorText, getUrlMatchRegex } from '@general/helpers/forms.helpers';
import { transformFormToProperty } from '@general/helpers/property.helpers';
import useCreateProperty from '@hooks/useCreateProperty';

const errorText = getErrorText('url');
const urlMatch = getUrlMatchRegex();

export const propertyValidationSchema = {
    title: Yup.string().required('Title is required.'),
    location: Yup.string().required('Location is required.'),
    subpremiseId: Yup.string()
        .max(255, 'Suite/Unit No cannot exceed 255 characters.')
        .nullable(),
    url: Yup.string().matches(urlMatch, errorText),
    listingStatus: Yup.string().required('Listing Status is required.'),
    squareFeet: Yup.number().required('Square Feet is required.'),
    purpose: Yup.string().required('Purpose of Space is required.'),
    density: Yup.string().required('Population Density is required.'),
    typeOfCenter: Yup.string().required('Type of Center is required.'),
    typeOfFacility: Yup.string().required('Type of Facility is required.'),
    description: Yup.string()
        .required('Description is required.')
        .max(1000, 'Description cannot exceed 1000 characters.'),
};

const PropertyFormFields = ({
    showSubmitButtons = true,
    submitButtonText = 'Save & Post',
    submitNewButtonText = 'Save & New',
    onCreatePropertySuccess,
}) => {
    const { validateForm, resetForm, values, setFieldValue, submitForm } = useFormikContext();
    const { openModal: openSuccessListingModal } = useSuccessListingModal();
    const [isSuccessfulUpdate, setIsSuccessfulUpdate] = useState(false);
    const [isErrorUpdate, setIsErrorUpdate] = useState(false);
    const [submitType, setSubmitType] = useState('post');
    const { createProperty, isLoading: isLoadingUpdate } = useCreateProperty({
        onCompleted: data => {
            setIsSuccessfulUpdate(true);
            setTimeout(() => {
                setIsSuccessfulUpdate(false);

                if (submitType === 'post') {
                    openSuccessListingModal(data.addProperty.id);
                } else {
                    resetForm();
                }

                if (onCreatePropertySuccess) {
                    onCreatePropertySuccess(data);
                }
            }, 2000);
        },
        onError: () => {
            setIsErrorUpdate(true);
            setTimeout(() => {
                setIsErrorUpdate(false);
            }, 3000);
        },
    });

    const saveProperty = () => {
        submitForm()
            .then(validateForm)
            .then(errors => {
                const isValid = Object.keys(errors).length === 0;
                if (isValid) {
                    const newProperty = transformFormToProperty(values);
                    createProperty({ variables: newProperty });
                }
            });
    };

    const saveAndNew = () => {
        setSubmitType('reset');
        saveProperty();
    };

    const saveAndPost = () => {
        setSubmitType('post');
        saveProperty();
    };

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <TextInput label="Property Title" name="title" placeholder="Property Name Here" required />
                </div>
            </div>
            <div className="row">
                <div className="col-12">
                    <AddressAutocomplete
                        label="Location"
                        name="location"
                        onAddressClear={() => {
                            setFieldValue('location', '');
                        }}
                        placeholder="Start typing an address..."
                        required
                    />
                </div>
            </div>

            <div className="row">
                <div className="col-12 col-md-6 col-lg-4">
                    <TextInput
                        label="Suite/Unit No."
                        name="subpremiseId"
                        placeholder="Suite/Unit No."
                        required={false}
                    />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                    <TextInput label="URL" name="url" placeholder="Paste URL Here" required={false} />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                    <SelectFancy
                        label="Listing Status"
                        name="listingStatus"
                        required
                        options={[
                            {
                                label: 'Sale',
                                value: 'SALE',
                            },
                            {
                                label: 'Lease',
                                value: 'LEASE',
                            },
                            {
                                label: 'Sublease',
                                value: 'SUBLEASE',
                            },
                        ]}
                    />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                    <TextInput
                        label="Square Feet"
                        name="squareFeet"
                        required
                        type="number"
                        onKeyDown={e => {
                            if (['.', '-', 'e'].includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                    />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                    <SelectFancy
                        label="Purpose of Space"
                        name="purpose"
                        required
                        options={[
                            {
                                label: 'Restaurant',
                                value: 'RESTAURANT',
                            },
                            {
                                label: 'Traditional Retail',
                                value: 'RETAIL',
                            },
                            {
                                label: 'Other Service',
                                value: 'OTHER',
                            },
                            {
                                label: 'Distribution',
                                value: 'DISTRIBUTION',
                            },
                        ]}
                    />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                    <SelectFancy
                        label="Population Density"
                        name="density"
                        required
                        options={[
                            {
                                label: 'Suburban',
                                value: 'SUBURBAN',
                            },
                            {
                                label: 'Urban',
                                value: 'URBAN',
                            },
                            {
                                label: 'Rural',
                                value: 'RURAL',
                            },
                        ]}
                    />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                    <SelectFancy
                        label="Type of Center"
                        name="typeOfCenter"
                        required
                        options={[
                            {
                                label: 'Mall',
                                value: 'MALL',
                            },
                            {
                                label: 'Power',
                                value: 'POWER',
                            },
                            {
                                label: 'Specialty/Lifestyle',
                                value: 'SPECIALTY',
                            },
                            {
                                label: 'Community/Neighborhood',
                                value: 'COMMUNITY',
                            },
                            {
                                label: 'Strip Center/Convenient Center',
                                value: 'STRIP',
                            },
                            {
                                label: 'Outlet Center',
                                value: 'OUTLET',
                            },
                            {
                                label: 'Mixed Use',
                                value: 'MIXED',
                            },
                            // {
                            //     label: 'Other',
                            //     value: 'OTHER',
                            // },
                            // {
                            //     label: 'None',
                            //     value: 'NONE',
                            // },
                        ]}
                    />
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                    <SelectFancy
                        label="Type of Facility"
                        name="typeOfFacility"
                        required
                        options={[
                            {
                                label: 'In-Line',
                                value: 'INLINE',
                            },
                            {
                                label: 'End Cap',
                                value: 'ENDCAP',
                            },
                            {
                                label: 'Freestanding',
                                value: 'FREESTANDING',
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-12">
                    <WYSIWYG
                        label="Property Description"
                        name="description"
                        initialValue={values.description}
                        required
                        charCount={1000}
                    />
                </div>
            </div>

            {/* Consider moving this up one level to NewPropertyModal since it's only used in the one place -Adam */}
            {showSubmitButtons && (
                <>
                    <DividerLightGreyStyled />
                    <div className="d-inline-flex flex-wrap align-items-center">
                        <div className="mx-auto mr-md-4 ml-md-0">
                            <ButtonOutlineWithStates
                                buttonClasses="createproperty"
                                onClick={() => saveAndNew()}
                                style={{ position: 'sticky', bottom: '1rem' }}
                                isSubmitting={isLoadingUpdate}
                                isSuccessful={isSuccessfulUpdate}
                                isError={isErrorUpdate}
                            >
                                {submitNewButtonText}
                            </ButtonOutlineWithStates>
                        </div>

                        <div className="mx-auto mx-md-0">
                            <SubmitButton
                                buttonClasses="createproperty"
                                onClick={() => saveAndPost()}
                                style={{ position: 'sticky', bottom: '1rem' }}
                                isSubmitting={isLoadingUpdate}
                                isSuccessful={isSuccessfulUpdate}
                                isError={isErrorUpdate}
                            >
                                {submitButtonText}
                            </SubmitButton>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

PropertyFormFields.propTypes = {
    showSubmitButtons: PropTypes.bool,
    submitButtonText: PropTypes.string,
    submitNewButtonText: PropTypes.string,
    onCreatePropertySuccess: PropTypes.func,
};

export default PropertyFormFields;
